import type { ServerToClientEvents } from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/event";
import type { PropsWithChildren } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { socket } from "../socket";
import { type ConversationsState, useConversationsContext } from "./ConversationsContext";
import { useLauncherContext } from "../../../../lib/client/contexts/LauncherContext";
import { useAuth } from "./DeviceContext";

const RETRY_TIMEOUT_MS = 15000;

function resolveMs(when: Date | undefined): number {
  return when ? when.getTime() - Date.now() : RETRY_TIMEOUT_MS;
}

type SocketProviderProps = PropsWithChildren<{
  tenantId: string;
  messengerId: string;
  messengerToken: string;
}>;

interface SocketContextValue {
  isConnected: boolean;
  isConnectionLimited: boolean;
  isRateLimited: boolean;
  retryAfter?: Date;
  token?: string;
  setCanConnect: () => void;
}

const SocketContext = createContext<SocketContextValue>({
  isConnected: false,
  isRateLimited: false,
  isConnectionLimited: false,
  setCanConnect: () => {},
});
export const useSocketContext = () => useContext(SocketContext);

function conversationsSelector(c: ConversationsState) {
  return Object.values(c.conversations).filter((conversation) => conversation.ticket_id).length > 0;
}

export default function SocketProvider({
  children,
  tenantId,
  messengerId,
  messengerToken,
}: SocketProviderProps) {
  const conversationsWithTicket = useConversationsContext(conversationsSelector);
  const [canConnect, setCanConnect] = useState(false);

  const [isConnecting, setIsConnecting] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [isConnectionLimited, setIsConnectionLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState<Date | undefined>(undefined);
  const messageTimeout = useRef<NodeJS.Timeout | undefined>(undefined);
  const connectionTimeout = useRef<NodeJS.Timeout | undefined>(undefined);

  const { api } = useLauncherContext();
  const { auth: token } = useAuth();

  const [lastToken, setLastToken] = useState<string | null>(token || null);

  if (
    // We must have a token to connect
    token &&
    !isConnected &&
    // We must only try to connect once
    !isConnecting &&
    // Only connect if the conversations have tickets, or after another component has instructed
    (conversationsWithTicket || canConnect)
  ) {
    // Set isConnecting so that future renders will not try and connect again
    setIsConnecting(true);
    socket.auth = { token, tenantId, messengerId, messengerToken };
    console.log("[deskpro-messenger] Connecting to socket", { tenantId, messengerId });
    socket.connect();
  }

  const reconnect = useCallback(async () => {
    console.log("[deskpro-messenger] Reconnecting to socket", { tenantId, messengerId });
    const newToken = await api.refreshAuthToken();
    if (newToken) {
      socket.auth = { token, tenantId, messengerId };
      socket.connect();
    }
  }, [api, messengerId, tenantId, token]);

  if (isConnected && token != lastToken) {
    setLastToken(token || null);
    socket.emit("updateAuth", token);
  }

  useEffect(() => {
    const onRateLimitEvent: ServerToClientEvents["rateLimit"] = (message, rate) => {
      setIsRateLimited(true);
      setRetryAfter(new Date(Date.now() + rate.msBeforeNext));
      clearTimeout(messageTimeout.current);
      messageTimeout.current = undefined;
    };

    socket.on("rateLimit", onRateLimitEvent);
    socket.on("connect_error", async (err) => {
      console.error(
        "[deskpro-messenger] Error connecting to the socket server. " + err.message,
        err,
      );
      if (/token has expired/i.test(err.message)) {
        await reconnect();
      } else if (/connection limit exceeded/i.test(err.message)) {
        const nextAttemptAt = new Date(Date.now() + RETRY_TIMEOUT_MS);
        clearTimeout(connectionTimeout.current);
        connectionTimeout.current = undefined;
        setIsConnectionLimited(true);
        setRetryAfter(nextAttemptAt);
      }
    });

    if (!connectionTimeout.current && isConnectionLimited) {
      connectionTimeout.current = setTimeout(() => {
        setRetryAfter(undefined);
        reconnect();
      }, resolveMs(retryAfter));
    }

    if (!messageTimeout.current && isRateLimited) {
      messageTimeout.current = setTimeout(() => {
        setIsRateLimited(false);
        setRetryAfter(undefined);
      }, resolveMs(retryAfter));
    }

    socket.on("connect", () => {
      console.log("[deskpro-messenger] Socket connected");
      setIsConnected(true);
      setIsConnectionLimited(false);
    });

    socket.on("disconnect", () => {
      console.log("[deskpro-messenger] Socket disconnected");
      setIsConnected(false);
    });

    return () => {
      socket.off("rateLimit", onRateLimitEvent);
      socket.off("connect_error");
      socket.off("connect");
      socket.off("disconnect");
      clearTimeout(messageTimeout.current);
      clearTimeout(connectionTimeout.current);
      messageTimeout.current = undefined;
      connectionTimeout.current = undefined;
    };
  }, [isConnectionLimited, isRateLimited, retryAfter, reconnect]);

  const value = useMemo(
    () => ({
      isConnected,
      token,
      isRateLimited,
      isConnectionLimited,
      retryAfter,
      setCanConnect: () => {
        setCanConnect(true);
      },
    }),
    [isConnected, token, isRateLimited, isConnectionLimited, retryAfter],
  );

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
